import {
  AUTH_EXPIRED_STATUS,
  NEW_TOKEN_EXPIRED_STATUS,
  logoutAndGoLogin,
  newRequest,
  refreshNewToken,
} from "./complexRequest"
import { ComplexRequestOption, ResponseType } from "./type"

/** Agent 类型：1 创作 Agent，2 短剧 Agent */
export enum AgentType {
  Creation = 1,
  Drama = 2,
}

export interface AgentSession {
  session_id: number
  type: AgentType
  title: string
  /** 会话简介（AI 通过 set_session_meta 生成的一句话描述） */
  summary: string
  /** 最近一条消息摘要 */
  last_message: string
  /** 会话封面（取最近生成的资产图） */
  cover: string
  created_at: number
  updated_at: number
}

/** 消息角色：user / assistant / tool（工具执行结果） */
export type AgentRole = "user" | "assistant" | "tool"

/** OpenAI 格式的工具调用（assistant 消息携带） */
export interface AgentToolCall {
  id: string
  type: "function"
  function: {
    name: string
    /** JSON 字符串 */
    arguments: string
  }
}

/**
 * 展示块：assistant 正文中以 :::block JSON 围栏内嵌的结构化产出，
 * 由前端 blockParser 解析渲染为交互卡片。
 */
export type AgentBlock =
  | {
      /** 方向选择（用户点选继续） */
      type: "options"
      title?: string
      items: { label: string; detail?: string }[]
    }
  | {
      /** 创作计划 */
      type: "plan"
      title?: string
      steps: { title: string; detail?: string }[]
    }
  | {
      /** 剧本 */
      type: "script"
      title: string
      logline?: string
      episodes: { title: string; synopsis: string }[]
    }
  | {
      /** 角色/场景资产 */
      type: "assets"
      /** 纯展示（不渲染生成按钮）：内容仅作预览/汇报，生成由 Agent 工具流程负责 */
      display_only?: boolean
      items: {
        kind: "character" | "scene" | "prop"
        name: string
        prompt: string
        /** 生成后由前端回填 */
        image?: string
        product_id?: number
      }[]
    }
  | {
      /**
       * 分镜表（纯展示的拍摄计划预览，不渲染生成按钮）：
       * 实际生成走三段管线工具流程（素材图 → 故事板 sheet 图 → generate_video 整段）
       */
      type: "storyboard"
      shots: {
        index: number
        scene: string
        shot_type?: string
        camera?: string
        duration?: number
        prompt: string
        dialogue?: string
      }[]
    }
  | {
      /** 可一键执行的生成动作 */
      type: "generate"
      mode: "image" | "video"
      prompt: string
      ref_images?: string[]
      seconds?: number
      resolution?: string
      ratio?: string
      note?: string
      /** 生成后由前端回填 */
      product_id?: number
      image?: string
      video?: string
    }
  | {
      /** 用户自己的角色卡选择（修改角色卡场景，点选发送） */
      type: "characters"
      title?: string
      items: {
        robot_id: number
        name: string
        avatar?: string
        intro?: string
        visibility?: string
      }[]
      page?: number
      has_more?: boolean
    }
  | {
      /** 成片/精选视频展示卡：HLS 内嵌播放 + 可选 MP4 下载直链 */
      type: "video"
      title?: string
      url: string
      poster?: string
      download_url?: string
    }

export interface AgentMessage {
  message_id: number
  session_id: number
  role: AgentRole
  /** 产生消息的 Agent 层：空=决策层/用户；writer/director/producer 等=子 Agent 气泡 */
  agent_name?: string
  content: string
  /** 模型思考过程（随消息落库，历史回显折叠面板；不入 LLM 上下文） */
  thinking?: string
  /** assistant 消息发起的工具调用 */
  tool_calls?: AgentToolCall[] | null
  /** role=tool 时对应的 tool_call id */
  tool_call_id?: string
  /** 结构化块的生成结果回填 */
  blocks?: AgentBlock[] | null
  skill_id?: string
  created_at: number
}

/** 技能定义（后端数据库管理：官方技能 + 用户沉淀技能） */
export interface AgentSkill {
  id: string
  /** 展示图标：emoji 或图片 URL */
  icon: string
  name: string
  description: string
  /** 技能商店卡片封面图 URL */
  cover?: string
  /** 技能商店卡片预览视频 URL（悬停播放） */
  video?: string
  /**
   * 技能落点：
   * - image / video：填入快速生成框（prompt 模板）
   * - agent / drama：进入对应 Agent 会话（服务端加载专属 SKILL.md）
   */
  target: "image" | "video" | "agent" | "drama"
  /** target=image/video 时的提示词模板 */
  prompt_template?: string
  /** 推荐置顶 */
  featured?: boolean
  /** 是否用户自建技能 */
  is_mine?: boolean
}

// ---------------------------------------------------------------------------
// REST 接口
// ---------------------------------------------------------------------------

// 创建 Agent 会话
export const createAgentSession = async (
  data: { type: AgentType; title?: string },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<{ session_id: number }>>(
    {
      url: "/client/agent/create",
      method: "post",
      data,
      ...option,
    },
    useFetch,
  )
}

// 会话列表（创作历史）
export const getAgentSessions = async (
  params?: { page?: number; page_size?: number },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<{ list: AgentSession[]; total: number }>>(
    {
      url: "/client/agent/sessions",
      params,
      ...option,
    },
    useFetch,
  )
}

/**
 * 会话消息记录（keyset 分页，默认最近 100 条升序）
 *
 * - before_id：向前翻更早历史（返回 id < before_id 的最近 limit 条）；
 * - after_id：增量拉取更新的消息，用于多端同步轮询（运行中消息逐条落库）；
 * - has_more 方向随请求：默认/before_id = 还有更早的，after_id = 还有更新的；
 * - running：该会话当前是否有 Agent 运行在跑（另一端发起的运行也算）。
 */
export const getAgentMessages = async (
  params: { session_id: number; limit?: number; before_id?: number; after_id?: number },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<
    ResponseType<{ session: AgentSession; list: AgentMessage[]; has_more: boolean; running: boolean }>
  >(
    {
      url: "/client/agent/messages",
      params,
      ...option,
    },
    useFetch,
  )
}

// 删除会话
export const deleteAgentSession = async (
  data: { session_id: number },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<any>>(
    {
      url: "/client/agent/session/delete",
      method: "post",
      data,
      ...option,
    },
    useFetch,
  )
}

// 技能目录（官方 + 我的，含商店预览字段）
// recommended / recommend_title：对话空白态的推荐技能与文案（后端可配置；
// 未下发时前端回退 featured 技能 + 默认文案）
export const getAgentSkills = async (
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<{
    list: AgentSkill[]
    official: AgentSkill[]
    mine: AgentSkill[]
    recommended?: AgentSkill[]
    recommend_title?: string
  }>>(
    {
      url: "/client/agent/skills",
      ...option,
    },
    useFetch,
  )
}

// 删除我的技能
export const deleteAgentSkill = async (
  data: { skill_id: string },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<any>>(
    {
      url: "/client/agent/skill/delete",
      method: "post",
      data,
      ...option,
    },
    useFetch,
  )
}

// 结构化块生成结果回填（如生成完成的 product_id / 图片 URL）
export const updateAgentMessageBlocks = async (
  data: { message_id: number; blocks: AgentBlock[] },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<any>>(
    {
      url: "/client/agent/message/blocks",
      method: "post",
      data,
      ...option,
    },
    useFetch,
  )
}

// ---------------------------------------------------------------------------
// 画布 / 生产工作区（flowData，结构与 PHP AgentToolsPkg 对齐，借鉴 Toonflow）
// ---------------------------------------------------------------------------

/** 资产生成状态 */
export type FlowItemState = "未生成" | "生成中" | "已完成" | "生成失败"

/** 资产库条目（角色/场景/道具） */
export interface FlowAssetItem {
  id: number
  name: string
  type: "role" | "scene" | "prop"
  /** 完整英文生图提示词 */
  prompt: string
  desc: string
  /** 生成的图片 URL */
  src: string
  state: FlowItemState
  product_id: number
}

/** 分镜面板条目 */
export interface FlowStoryboardItem {
  id: number
  index: number
  /** 画面描述（用户语言） */
  videoDesc: string
  /** 完整英文视频提示词 */
  prompt: string
  /** 首帧冻结关键帧英文提示词（分镜图生成用；缺省回退 prompt） */
  imagePrompt?: string
  /** 分组（同组连续剪辑） */
  track: string
  /** 推荐时长（秒），模型档位 5/10/15 */
  duration: number
  associateAssetsIds: number[]
  /** 分镜图 URL */
  src: string
  /** 分镜视频 URL */
  videoSrc: string
  state: FlowItemState
  product_id: number
  video_product_id: number
  /** 服务端入队时锁定并持久化到条目的生成参数（出队后重生成仍可读取，不退回默认模型） */
  sheet_model_id?: number
  video_model_id?: number
  image_ratio?: string
}

/** 生成队列条目（Agent 工具入队，前端执行器消费） */
export interface FlowQueueItem {
  kind: "asset" | "storyboard" | "storyboard_video"
  id: number
  created_at: number
  /** 服务端锁定的生成模型：storyboard = 平台指定的多格 sheet 专用模型（gpt 系，其他模型格子编号错乱）；
   * storyboard_video = LLM 按能力表 Policy 核对后选定（真人写实内容不可用 seedance 系）。缺省 = 前端自动挑 */
  model_id?: number
  /** 服务端锁定的画布比例（单一事实源：比例是系统参数，prompt 里禁写比例词——两处冲突会让模型无视网格布局）。
   * storyboard = 9:16 竖幅 sheet。缺省 = 后端默认 */
  image_ratio?: string
  /** 服务端标记的重新生成：条目已有结果时的入队 = 用户确认过的重做，执行器须跳过"已有结果视为成功"的防重短路 */
  force?: boolean
}

/** PPT 幻灯片（ppt-deck 技能：Agent 逐页手写 SVG，导出为原生可编辑 PPTX） */
export interface PptSlide {
  /** 页码（1 起） */
  no: number
  /** 页面 slug（导出文件名用） */
  name: string
  /** 完整 SVG 文档（ppt169 为 viewBox 0 0 1280 720） */
  svg: string
  /** 演讲者备注（markdown，可空） */
  notes?: string
}

/** PPT 演示文稿工作区（与 PHP AgentToolsPkg 的 pptDeck 结构对齐） */
export interface PptDeck {
  title: string
  /** 版式：ppt169 = 16:9（1280x720） */
  format: string
  slides: PptSlide[]
}

export const emptyPptDeck = (): PptDeck => ({ title: "", format: "ppt169", slides: [] })

/** 画布节点类型：text/image/video 为基础节点；audio=音频（TTS/上传）、panorama=全景图、generate=生成器、director=导演台（3D 布景） */
export type FlowCanvasNodeType = "text" | "image" | "video" | "audio" | "panorama" | "generate" | "director"

/** 生成器节点元数据（generate 节点；panorama 节点 AI 生成时也复用 prompt/status） */
export interface FlowGenMeta {
  mode: "image" | "video"
  prompt: string
  /** 指定生成模型；缺省用该类型第一个 */
  model_id?: number
  /** 图片尺寸档（取自模型 image_support[].size，用于计价展示） */
  size?: string
  /** 画面比例（如 "16:9"；注入提示词，后端接口无比例参数） */
  ratio?: string
  /** 图片张数（1-4，前端并发创建多个任务） */
  count?: number
  /** 视频时长（秒） */
  seconds?: number
  /** 视频分辨率，如 "720p" */
  resolution?: string
  status?: "idle" | "running" | "failed"
  error?: string
  /** 多张结果（count>1 时；content 为当前主图） */
  results?: { url: string; product_id: number }[]
}

// ---------------------------------------------------------------------------
// 导演台（3D 布景：全景环境 + 角色/道具 + 多机位，截图落画布节点）
// ---------------------------------------------------------------------------

/** 场景对象：figure=素体人偶（基础几何拼装），card=图片站牌（billboard），box/sphere/cylinder/cone=几何道具 */
export type DirectorObjectKind = "figure" | "card" | "box" | "sphere" | "cylinder" | "cone"

export interface DirectorObject {
  id: string
  kind: DirectorObjectKind
  name: string
  /** card 的贴图（支持 local: 引用） */
  image?: string
  position: [number, number, number]
  rotation: [number, number, number]
  scale: [number, number, number]
  color?: string
  visible?: boolean
}

export interface DirectorCamera {
  id: string
  name: string
  fov: number
  position: [number, number, number]
  target: [number, number, number]
}

/** 导演台场景数据（存于 director 节点） */
export interface DirectorSceneData {
  /** 全景环境图（equirect 贴天空球；支持 local: 引用） */
  background?: string
  backgroundColor?: string
  showGround?: boolean
  objects: DirectorObject[]
  cameras: DirectorCamera[]
  activeCameraId?: string
  /** 截图画幅比例，如 "16:9" */
  aspect?: string
}

export const emptyDirectorScene = (): DirectorSceneData => ({
  backgroundColor: "#1a1c26",
  showGround: true,
  objects: [],
  cameras: [
    { id: "cam_1", name: "机位 1", fov: 50, position: [0, 1.6, 5.5], target: [0, 1, 0] },
  ],
  activeCameraId: "cam_1",
  aspect: "16:9",
})

/** 音频节点元数据（TTS 合成参数与状态） */
export interface FlowAudioMeta {
  voice_id?: number
  voice_name?: string
  /** 合成文本 */
  text?: string
  /** 音频时长（秒） */
  duration?: number
  status?: "idle" | "running" | "failed"
  error?: string
}

/**
 * 自由画布节点（infinite-canvas 式工作台）：
 * text=markdown 卡片；image/video/audio/panorama 的 content 为媒体 URL；
 * generate=生图/生视频节点（生成结果写回本节点 content，支持重复生成）。
 */
export interface FlowCanvasNode {
  id: number
  type: FlowCanvasNodeType
  title?: string
  content: string
  /**
   * 工作区文本（script/scriptPlan/storyboardTable/postPlan）的画布投影标记：
   * Agent 用 set_flowData_text 重写文本后，画布加载时按此标记找到对应节点同步内容
   */
  sourceKey?: "script" | "scriptPlan" | "storyboardTable" | "postPlan"
  /** video 封面图 */
  poster?: string
  x: number
  y: number
  /** 节点宽度 */
  w?: number
  product_id?: number
  /** 生成器参数（generate / panorama 节点） */
  gen?: FlowGenMeta
  /** 音频参数（audio 节点） */
  audio?: FlowAudioMeta
  /** 导演台场景（director 节点） */
  director?: DirectorSceneData
}

/** 画布连线：上游（source）输出作为下游（target）生成时的输入（图片作参考图，文本并入提示词） */
export interface FlowCanvasEdge {
  id: number
  source: number
  target: number
}

/** 生产工作区数据 */
export interface FlowData {
  /** 剧本 */
  script: string
  /** 导演拍摄计划 */
  scriptPlan: string
  /** 分镜表（markdown） */
  storyboardTable: string
  /** 后期方案（JSON 文本：{film_duration, edl, grade, source_music, cues, repairs, findings, notes}；旧数据可能缺失） */
  postPlan?: string
  assets: FlowAssetItem[]
  storyboard: FlowStoryboardItem[]
  queue: FlowQueueItem[]
  /** 自由画布节点 */
  nodes: FlowCanvasNode[]
  /** 节点连线 */
  edges: FlowCanvasEdge[]
  /** PPT 演示文稿工作区（ppt-deck 技能；旧数据可能缺失） */
  pptDeck?: PptDeck
}

export const emptyFlowData = (): FlowData => ({
  script: "",
  scriptPlan: "",
  storyboardTable: "",
  postPlan: "",
  assets: [],
  storyboard: [],
  queue: [],
  nodes: [],
  edges: [],
  pptDeck: emptyPptDeck(),
})

// 对话展示块的生成结果写回工作区（资产按名字匹配 / 分镜按 index），保持块与画布状态一致
export const syncAssetResult = async (
  data: {
    session_id: number
    kind: "asset" | "storyboard" | "storyboard_video"
    name?: string
    index?: number
    image_url?: string
    video_url?: string
    product_id?: number
  },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<{ ok: boolean; note?: string }>>(
    {
      url: "/client/agent/canvas/asset_result",
      method: "post",
      data,
      ...option,
    },
    useFetch,
  )
}

// 读取工作区（session_id 传 0 表示用户的自由画布）
export const getAgentCanvas = async (
  params: { session_id?: number },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<{ data: FlowData | null; title: string; updated_at: number }>>(
    {
      url: "/client/agent/canvas",
      params,
      ...option,
    },
    useFetch,
  )
}

// 保存工作区（全量覆盖；前端执行器回写生成结果时使用）
export const saveAgentCanvas = async (
  data: { session_id?: number; title?: string; data: FlowData },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<{ updated_at: number }>>(
    {
      url: "/client/agent/canvas/save",
      method: "post",
      data,
      ...option,
    },
    useFetch,
  )
}

// ---------------------------------------------------------------------------
// 分享（对话/画布公开链接）
// ---------------------------------------------------------------------------

/** 创建分享链接（同会话同类型幂等复用 share_key） */
/**
 * 停止当前会话的 agent 运行：后端写取消标记并立即释放会话锁（run 在步边界自行终止）。
 * 不调用时"停止"只断前端流，后端继续跑且持有锁，随后发新消息会 409 进同步视图
 */
export const stopAgentChat = async (
  data: { session_id: number },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<{ stopped: boolean }>>(
    {
      url: "/client/agent/chat/stop",
      method: "post",
      data,
      ...option,
    },
    useFetch,
  )
}

export const createAgentShare = async (
  data: { session_id: number; kind: "chat" | "canvas" },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<{ share_key: string; kind: "chat" | "canvas" }>>(
    {
      url: "/client/agent/share/create",
      method: "post",
      data,
      ...option,
    },
    useFetch,
  )
}

/** 分享页角色卡（create_character/update_character 工具调用提炼的展示字段） */
export interface ShareCharacter {
  name: string
  avatar: string
  /** 1=男 2=女 3=其他 */
  gender: number
  /** 对外介绍文案 */
  scene: string
  /** 开场白（markdown） */
  greeting: string
  /** 背景图（可空） */
  background: string
  is_update: boolean
  /** 建卡成功且公开可见时为角色 id（可跳转聊天），否则 0 */
  robot_id: number
  /**
   * 以下为可选详情字段：分享接口历史版本为防泄露创作者资产做过裁剪，
   * 后端 /client/agent/share/view 若返回则前端在详情 Tab 中展示，未返回时对应 Tab 不出现。
   */
  /** 记忆 / 设定条目 */
  attributes?: Array<{ key: string; val: string }>
  /** 提示词 / 示例对话（对应 robot 详情页 common.prompt 字段） */
  conversations?: string
  /** 玩法指南 */
  guide_for_use?: string
}

/** 分享页数据（/client/agent/share/view 公开接口返回，SSR 直接 fetch） */
export interface AgentShareData {
  kind: "chat" | "canvas"
  /** 分享者创建分享时的界面语言（分享页固定按此语言渲染） */
  lang?: string
  session: { title: string; summary: string; type: AgentType; cover: string; updated_at: number }
  /** kind=chat */
  messages?: Array<{
    role: "user" | "assistant"
    agent_name?: string
    content: string
    blocks?: AgentBlock[] | null
    /** 该条消息发起的角色卡（只读渲染） */
    characters?: ShareCharacter[]
    created_at: number
  }>
  /** kind=canvas（输出已裁剪：无提示词等内部数据） */
  canvas?: {
    script: string
    scriptPlan: string
    storyboardTable: string
    /** 后期方案 JSON 文本（旧分享/后端未升级时缺失） */
    postPlan?: string
    assets: Array<{ name: string; type: string; desc: string; src: string }>
    storyboard: Array<{ index: number; track: string; duration: number; videoDesc: string; src: string; videoSrc: string }>
    nodes: Array<{ type: "text" | "image" | "video" | "audio"; title: string; content: string; poster: string }>
    pptDeck: { title: string; format: string; slides: PptSlide[] }
  }
}

// ---------------------------------------------------------------------------
// SSE 流式对话（AG-UI 协议子集：https://docs.ag-ui.com/concepts/events）
// ---------------------------------------------------------------------------

/** SSE 事件：与 PHP AgentController 输出的 AG-UI 事件对齐 */
export type AgentStreamEvent =
  | { type: "RUN_STARTED"; threadId: string; runId: string }
  | { type: "STEP_STARTED"; stepName: string }
  | { type: "TEXT_MESSAGE_START"; messageId: string; role: string; name?: string }
  | { type: "TEXT_MESSAGE_CONTENT"; messageId: string; delta: string }
  | { type: "TEXT_MESSAGE_END"; messageId: string }
  /** 模型思考过程增量（流式实时展示；完整思考随消息落库，刷新后经 messages 接口回显） */
  | { type: "THINKING_TEXT_MESSAGE_CONTENT"; messageId: string; delta: string }
  | { type: "TOOL_CALL_START"; toolCallId: string; toolCallName: string; parentMessageId?: string }
  | { type: "TOOL_CALL_ARGS"; toolCallId: string; delta: string }
  | { type: "TOOL_CALL_END"; toolCallId: string }
  | {
      type: "CUSTOM"
      name: "message_saved" | "tool_result" | "flow_updated" | string
      value: any
    }
  | { type: "RUN_FINISHED"; threadId: string; runId: string; outcome?: string }
  | { type: "RUN_ERROR"; message: string }

/** 客户端生成工具执行结果（generate_image / generate_video 由前端确认执行后回传） */
export interface AgentToolResult {
  tool_call_id: string
  /** JSON 字符串结果 */
  content: string
}

export interface AgentChatHandlers {
  onEvent: (event: AgentStreamEvent) => void
  /** 流意外断开（网络错误等） */
  onAbort?: (err: unknown) => void
  /**
   * 会话已在别处运行（服务端会话锁返回 409）：本次请求未消耗任何配额，
   * 调用方应转入轮询同步视图跟随那次运行，而不是报错。
   */
  onConflict?: () => void
}

/**
 * Agent 流式对话：POST /client/agent/chat，SSE 返回（三层协作事件流）。
 * content 为新用户消息；tool_results 为客户端生成工具结果回传（续跑循环）。
 * 返回 abort 函数用于取消。
 */
export const agentChatStream = async (
  data: {
    session_id: number
    content?: string
    skill_id?: string
    tool_results?: AgentToolResult[]
  },
  { onEvent, onAbort, onConflict }: AgentChatHandlers,
) => {
  const controller = new AbortController()

  const run = async (isRetry = false): Promise<void> => {
    const response = await newRequest<Response>(
      {
        url: "/client/agent/chat",
        method: "post",
        data,
        signal: controller.signal,
        isStream: true,
      } as any,
      true,
    )

    // 业务错误走 JSON（HTTP 仍是 200，业务码在 body.status）：会话锁 409 表示
    // 该会话已有运行在跑，交给 onConflict 转同步视图；其余按可读错误上报
    const isJson = response.headers.get("content-type")?.includes("json")
    if (!response.ok || !response.body || isJson) {
      let message = `HTTP ${response.status}`
      try {
        const json = await response.json()
        if (json?.status === 409 || json?.data?.running) {
          onConflict?.()
          return
        }
        // newToken 过期（-102）：isStream 请求绕过了 newRequest 的自动刷新重试，
        // 这里补齐同一语义——静默刷新后重试一次，用户无感（不弹"token 过期"）
        if (json?.status === NEW_TOKEN_EXPIRED_STATUS && !isRetry) {
          const refreshed = await refreshNewToken()
          if (refreshed) return run(true)
        }
        // 登录态彻底失效（102）：与非流式路径一致，清会话并跳登录页
        if (json?.status === AUTH_EXPIRED_STATUS) {
          await logoutAndGoLogin()
          return
        }
        message = json?.message || json?.data?.info || message
      } catch {}
      onEvent({ type: "RUN_ERROR", message })
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder("utf-8")
    let buffer = ""

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE 帧以空行分隔；兼容 \r\n
      const frames = buffer.split(/\r?\n\r?\n/)
      buffer = frames.pop() ?? ""
      for (const frame of frames) {
        for (const line of frame.split(/\r?\n/)) {
          if (!line.startsWith("data:")) continue
          const payload = line.slice(5).trim()
          if (!payload || payload === "[DONE]") continue
          try {
            onEvent(JSON.parse(payload) as AgentStreamEvent)
          } catch {
            // 忽略无法解析的帧
          }
        }
      }
    }
  }

  const promise = run().catch((err) => {
    if (!controller.signal.aborted) onAbort?.(err)
  })

  return {
    abort: () => controller.abort(),
    promise,
  }
}
